import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';
import { palette } from '../../tokens/palette';

export type FormActionsAlign = 'between' | 'end' | 'start' | 'center';

export interface FormActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Stick the bar to the bottom of its scroll container. */
  sticky?: boolean;
  /** How buttons are justified. Defaults to `'between'`. */
  align?: FormActionsAlign;
  /** Show a top border divider. Defaults to `true`. */
  divider?: boolean;
  semanticId?: string;
  children: React.ReactNode;
  /**
   * Strip the built-in horizontal padding (left/right `32px`).
   * Use when the bar is embedded inside a container that already provides horizontal spacing
   * (e.g. a bordered `Surface` wrapper with its own padding).
   */
  noHorizontalPadding?: boolean;
}

const ALIGN_MAP: Record<FormActionsAlign, string> = {
  between: 'space-between',
  end: 'flex-end',
  start: 'flex-start',
  center: 'center',
};

const TRANSIENT = ['$sticky', '$align', '$divider', '$noHorizontalPadding'] as const;
const shouldForward = (prop: string | number): boolean =>
  !TRANSIENT.includes(prop as (typeof TRANSIENT)[number]);

const StyledFormActions = styled.div.withConfig({ shouldForwardProp: shouldForward })<{
  $sticky: boolean;
  $align: FormActionsAlign;
  $divider: boolean;
  $noHorizontalPadding: boolean;
}>`
  box-sizing: border-box;
  width: 100%;
  padding: ${rem(20)} ${({ $noHorizontalPadding }) => ($noHorizontalPadding ? '0' : rem(32))};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(12)};
  justify-content: ${({ $align }) => ALIGN_MAP[$align]};
  background-color: var(--ui-surface, ${palette.White});
  border-bottom-right-radius: ${rem(10)};
  border-bottom-left-radius: ${rem(10)};

  ${({ $divider }) =>
    $divider &&
    css`
      border-top: ${rem(1)} solid var(--ui-border-muted, ${palette.CulturedWhite});
    `}

  ${({ $sticky }) =>
    $sticky &&
    css`
      position: sticky;
      bottom: 0;
      z-index: 1;
    `}
`;

export const FormActions = React.forwardRef<HTMLDivElement, FormActionsProps>(
  function FormActions(
    { sticky = false, align = 'between', divider = true, noHorizontalPadding = false, semanticId, children, ...rest },
    ref,
  ) {
    const semanticProps = getSemanticAttributes(semanticId);

    return (
      <StyledFormActions
        ref={ref}
        $sticky={sticky}
        $align={align}
        $divider={divider}
        $noHorizontalPadding={noHorizontalPadding}
        {...semanticProps}
        {...rest}
      >
        {children}
      </StyledFormActions>
    );
  },
);

FormActions.displayName = 'FormActions';
